/**
 * Formats a number for display in the interface language.
 *
 * The locale is passed in rather than read here: these utils run outside a
 * component, where reaching for the Nuxt i18n instance is not safe. Callers in
 * a component pass `this.$i18n.locale`; a registry type passes
 * `this.app.$i18n.locale.value`.
 *
 * Calling `Number.prototype.toLocaleString()` or `Intl.NumberFormat` with no
 * locale is what this exists to replace: those read the *browser's* language,
 * so a German browser rendered `1.234` into an English interface, where it
 * reads as one-point-two-three-four rather than one thousand. A lint rule
 * rejects the no-locale forms.
 *
 * This is for numbers the interface itself produces — counts, totals,
 * aggregations. A **number field's** value is formatted by
 * `database/utils/number.js` instead, which derives its separators from the
 * field's own settings: that is user configuration on the data, not a property
 * of the interface language.
 *
 * @param {number|string} value The number to format.
 * @param {String|null} locale A BCP-47 tag or bare language; falls back to
 *   English when absent or unusable.
 * @param {Object} options Passed through to `Intl.NumberFormat`.
 * @returns {String} The formatted number, '' when there is no value, or the
 *   input as a string when it is not a finite number.
 */
export const formatNumber = (value, locale, options = {}) => {
  if (value === null || value === undefined || value === '') {
    return ''
  }
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) {
    return String(value)
  }
  try {
    return new Intl.NumberFormat(locale || 'en', options).format(numeric)
  } catch {
    // A malformed tag makes Intl throw; a cell still has to render.
    return new Intl.NumberFormat('en', options).format(numeric)
  }
}

export const rounder = (digits) => {
  return parseInt('1' + Array(digits + 1).join('0'))
}

export const floor = (n, digits = 0) => {
  const r = rounder(digits)
  return Math.floor(n * r) / r
}

export const ceil = (n, digits = 0) => {
  const r = rounder(digits)
  return Math.ceil(n * r) / r
}

export const clamp = (value, min, max) => {
  return Math.max(min, Math.min(value, max))
}

export const sum = (arr, { strict = false } = {}) => {
  return arr.reduce((total, val) => {
    const num = Number(val)
    if (Number.isFinite(num)) {
      return total + num
    } else if (strict) {
      throw new Error(`Invalid number: ${val}`)
    }
    return total
  }, 0)
}

export const avg = (arr, { strict = false } = {}) => {
  let validNumbers = 0
  const _sum = arr.reduce((total, val) => {
    const num = Number(val)
    if (Number.isFinite(num)) {
      validNumbers++
      return total + num
    } else if (strict) {
      throw new Error(`Invalid number: ${val}`)
    }
    return total
  }, 0)
  if (validNumbers > 0) {
    return _sum / validNumbers
  }
  return _sum
}
